/**
 * DocTypesAdmin - Super Admin page for managing document types and parameters
 *
 * Features:
 * - View all document types with default retrieval parameters
 * - Create new document type
 * - Edit document type parameters (type_boost, k_default, score thresholds, field weights, etc.)
 * - Delete document type
 * - Parameter sliders for tuning retrieval behavior
 *
 * Permissions: Super Admin only (Global airline admin)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase';
import { useAirline } from '../../contexts/AirlineContext';
import { DocType, DocumentTypeCode } from '../../lib/types';
import {
  Plus,
  Edit,
  Trash2,
  FileText,
  X,
  Info,
} from 'lucide-react';

// =====================================================================
// Types
// =====================================================================

interface DocTypeWithStats extends DocType {
  doc_count: number;
  override_count: number;
}

interface DocTypeFormData {
  code: DocumentTypeCode;
  label: string;
  type_boost: number;
  k_default: number;
  score_threshold_default: number;
  alpha_hybrid_default: number;
  lambda_mmr_default: number;
  field_weight_title: number;
  field_weight_body: number;
  field_weight_caption: number;
  recency_decay_default: number;
  assessment_boost_default: number;
}

// =====================================================================
// Validation Schema
// =====================================================================

const docTypeSchema = z.object({
  code: z.string().min(2, 'Code must be at least 2 characters'),
  label: z.string().min(3, 'Label must be at least 3 characters'),
  type_boost: z.number().min(-1).max(1),
  k_default: z.number().int().min(1).max(50),
  score_threshold_default: z.number().min(0).max(1),
  alpha_hybrid_default: z.number().min(0).max(1),
  lambda_mmr_default: z.number().min(0).max(1),
  field_weight_title: z.number().min(0).max(5),
  field_weight_body: z.number().min(0).max(5),
  field_weight_caption: z.number().min(0).max(5),
  recency_decay_default: z.number().min(0).max(0.01),
  assessment_boost_default: z.number().min(-1).max(1),
});

type DocTypeSchemaType = z.infer<typeof docTypeSchema>;

// =====================================================================
// Main Component
// =====================================================================

export default function DocTypesAdmin() {
  const { isSuperAdmin } = useAirline();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingDocType, setEditingDocType] = useState<DocTypeWithStats | null>(null);
  const [deletingDocType, setDeletingDocType] = useState<DocTypeWithStats | null>(null);

  // ===================================================================
  // Fetch Document Types with Stats
  // ===================================================================

  const { data: docTypes, isLoading } = useQuery<DocTypeWithStats[]>({
    queryKey: ['doc-types-admin'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('doc_types')
        .select(`
          *,
          doc_library(count),
          airline_doc_type_overrides(count)
        `)
        .order('code');

      if (error) throw error;

      return (data || []).map((docType: any) => ({
        ...docType,
        doc_count: docType.doc_library?.[0]?.count || 0,
        override_count: docType.airline_doc_type_overrides?.[0]?.count || 0,
      }));
    },
    enabled: isSuperAdmin,
  });

  // ===================================================================
  // Mutations
  // ===================================================================

  const createMutation = useMutation({
    mutationFn: async (formData: DocTypeFormData) => {
      const { data, error } = await supabase
        .from('doc_types')
        .insert(formData)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-types-admin'] });
      setShowCreateModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }: { id: string; formData: DocTypeFormData }) => {
      const { data, error } = await supabase
        .from('doc_types')
        .update(formData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-types-admin'] });
      setEditingDocType(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('doc_types')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-types-admin'] });
      setDeletingDocType(null);
    },
  });

  // ===================================================================
  // Permission Check
  // ===================================================================

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need super admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Document Types Management</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage document types and configure default retrieval parameters
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Add Document Type
        </button>
      </div>

      {/* Document Types Table */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading document types...</div>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Code
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Boost
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  K
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Docs
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Overrides
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {docTypes?.map((docType) => (
                <tr key={docType.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-xs font-medium text-gray-900 truncate">{docType.label}</span>
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      {docType.code}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        docType.type_boost > 0
                          ? 'bg-green-100 text-green-800'
                          : docType.type_boost < 0
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {docType.type_boost > 0 ? '+' : ''}
                      {docType.type_boost.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-900">
                    {docType.k_default}
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-900">
                    {docType.doc_count}
                  </td>
                  <td className="px-2 py-2 text-center text-xs text-gray-900">
                    {docType.override_count}
                  </td>
                  <td className="px-2 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => setEditingDocType(docType)}
                        className="p-1 text-sky-600 hover:bg-sky-50 rounded"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setDeletingDocType(docType)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <DocTypeFormModal
          title="Create New Document Type"
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
          error={createMutation.error as Error | null}
        />
      )}

      {/* Edit Modal */}
      {editingDocType && (
        <DocTypeFormModal
          title="Edit Document Type"
          docType={editingDocType}
          onClose={() => setEditingDocType(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editingDocType.id, formData: data })}
          isLoading={updateMutation.isPending}
          error={updateMutation.error as Error | null}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingDocType && (
        <DeleteConfirmationModal
          docType={deletingDocType}
          onClose={() => setDeletingDocType(null)}
          onConfirm={() => deleteMutation.mutate(deletingDocType.id)}
          isLoading={deleteMutation.isPending}
          error={deleteMutation.error as Error | null}
        />
      )}
    </div>
  );
}

// =====================================================================
// Document Type Form Modal
// =====================================================================

interface DocTypeFormModalProps {
  title: string;
  docType?: DocTypeWithStats;
  onClose: () => void;
  onSubmit: (data: DocTypeFormData) => void;
  isLoading: boolean;
  error: Error | null;
}

const DocTypeFormModal: React.FC<DocTypeFormModalProps> = ({
  title,
  docType,
  onClose,
  onSubmit,
  isLoading,
  error,
}) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<DocTypeSchemaType>({
    resolver: zodResolver(docTypeSchema),
    defaultValues: {
      code: docType?.code || '',
      label: docType?.label || '',
      type_boost: docType?.type_boost || 0,
      k_default: docType?.k_default || 10,
      score_threshold_default: docType?.score_threshold_default || 0.75,
      alpha_hybrid_default: docType?.alpha_hybrid_default || 0.7,
      lambda_mmr_default: docType?.lambda_mmr_default || 0.6,
      field_weight_title: docType?.field_weight_title || 1.3,
      field_weight_body: docType?.field_weight_body || 1.0,
      field_weight_caption: docType?.field_weight_caption || 0.7,
      recency_decay_default: docType?.recency_decay_default || 0.0008,
      assessment_boost_default: docType?.assessment_boost_default || 0.0,
    },
  });

  const handleFormSubmit = (data: DocTypeSchemaType) => {
    // Cast code to DocumentTypeCode
    onSubmit({
      ...data,
      code: data.code as DocumentTypeCode,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Error</p>
              <p className="text-sm">{error.message}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('code')}
                  disabled={!!docType}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 disabled:bg-gray-100"
                  placeholder="CoreSystem"
                />
                {errors.code && (
                  <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register('label')}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                  placeholder="Core System Documents"
                />
                {errors.label && (
                  <p className="mt-1 text-sm text-red-600">{errors.label.message}</p>
                )}
              </div>
            </div>

            {/* Retrieval Parameters */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                Retrieval Parameters
                <Info size={20} className="text-gray-400" />
              </h3>

              <div className="space-y-4">
                {/* Type Boost */}
                <ParameterSlider
                  label="Type Boost"
                  name="type_boost"
                  control={control}
                  min={-1}
                  max={1}
                  step={0.05}
                  description="Score multiplier for this document type (-1 to +1)"
                  watch={watch}
                />

                {/* K Default */}
                <ParameterSlider
                  label="K Default"
                  name="k_default"
                  control={control}
                  min={1}
                  max={50}
                  step={1}
                  description="Default number of results to return (1-50)"
                  watch={watch}
                />

                {/* Score Threshold */}
                <ParameterSlider
                  label="Score Threshold"
                  name="score_threshold_default"
                  control={control}
                  min={0}
                  max={1}
                  step={0.05}
                  description="Minimum similarity score to include result (0-1)"
                  watch={watch}
                />

                {/* Alpha Hybrid */}
                <ParameterSlider
                  label="Alpha Hybrid"
                  name="alpha_hybrid_default"
                  control={control}
                  min={0}
                  max={1}
                  step={0.05}
                  description="Vector vs lexical search weight (0=lexical only, 1=vector only)"
                  watch={watch}
                />

                {/* Lambda MMR */}
                <ParameterSlider
                  label="Lambda MMR"
                  name="lambda_mmr_default"
                  control={control}
                  min={0}
                  max={1}
                  step={0.05}
                  description="Diversity vs relevance balance (0=diversity, 1=relevance)"
                  watch={watch}
                />
              </div>
            </div>

            {/* Field Weights */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Field Weights</h3>

              <div className="space-y-4">
                <ParameterSlider
                  label="Title Weight"
                  name="field_weight_title"
                  control={control}
                  min={0}
                  max={5}
                  step={0.1}
                  description="Importance of document title in search"
                  watch={watch}
                />

                <ParameterSlider
                  label="Body Weight"
                  name="field_weight_body"
                  control={control}
                  min={0}
                  max={5}
                  step={0.1}
                  description="Importance of document body in search"
                  watch={watch}
                />

                <ParameterSlider
                  label="Caption Weight"
                  name="field_weight_caption"
                  control={control}
                  min={0}
                  max={5}
                  step={0.1}
                  description="Importance of captions in search"
                  watch={watch}
                />
              </div>
            </div>

            {/* Advanced Parameters */}
            <div className="border-t pt-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Advanced Parameters</h3>

              <div className="space-y-4">
                <ParameterSlider
                  label="Recency Decay"
                  name="recency_decay_default"
                  control={control}
                  min={0}
                  max={0.01}
                  step={0.0001}
                  description="How much to prioritize recent documents (0-0.01)"
                  watch={watch}
                />

                <ParameterSlider
                  label="Assessment Boost"
                  name="assessment_boost_default"
                  control={control}
                  min={-1}
                  max={1}
                  step={0.05}
                  description="Boost for assessment-linked documents (-1 to +1)"
                  watch={watch}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(handleFormSubmit)}
            disabled={isLoading}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : docType ? 'Update Document Type' : 'Create Document Type'}
          </button>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Parameter Slider Component
// =====================================================================

interface ParameterSliderProps {
  label: string;
  name: keyof DocTypeFormData;
  control: any;
  min: number;
  max: number;
  step: number;
  description: string;
  watch: any;
}

const ParameterSlider: React.FC<ParameterSliderProps> = ({
  label,
  name,
  control,
  min,
  max,
  step,
  description,
  watch,
}) => {
  const value = watch(name);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <span className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
          {typeof value === 'number' ? value.toFixed(step < 0.01 ? 4 : 2) : value}
        </span>
      </div>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={field.value}
            onChange={(e) => field.onChange(parseFloat(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-600"
          />
        )}
      />
      <p className="mt-1 text-xs text-gray-500">{description}</p>
    </div>
  );
};

// =====================================================================
// Delete Confirmation Modal
// =====================================================================

interface DeleteConfirmationModalProps {
  docType: DocTypeWithStats;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  error: Error | null;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  docType,
  onClose,
  onConfirm,
  isLoading,
  error,
}) => {
  const hasResources = docType.doc_count > 0 || docType.override_count > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Delete Document Type?</h2>

        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error.message}</p>
          </div>
        )}

        <p className="text-gray-600 mb-4">
          Are you sure you want to delete <strong>{docType.label}</strong>?
        </p>

        {hasResources && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p className="font-medium">Warning</p>
            <p className="text-sm">
              This document type has {docType.doc_count} document(s) and {docType.override_count}{' '}
              airline override(s). Deleting will cascade to all related records.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {isLoading ? 'Deleting...' : 'Delete Document Type'}
          </button>
        </div>
      </div>
    </div>
  );
};
